import { Box, Text, Divider, VStack, HStack } from '@chakra-ui/react';
import { useColorModeValue } from '@chakra-ui/react';
import { WarningIcon } from '@chakra-ui/icons';
import { GoShieldCheck } from 'react-icons/go';
import { useContext } from 'react';
import { MeContext } from '../../../context/MeContext';
import { useState } from 'react';
import { useMfaApi } from '../../../hooks/useMfaApi';
import { useMfaToast } from '../../../hooks/useMfaToast';
import DisableMfaModal from '../../modals/DisableMfaModal';
import MfaOperationModal from '../../modals/MfaOperationModal';

function MfaSettings() {
  const modeBoxBg = useColorModeValue('gradient', 'secondaryDark');
  const modeTextBg = useColorModeValue('quaternaryBackground', 'primaryDark06');
  const modeBorderColor = useColorModeValue('primaryBackground', 'primaryDark');
  const meCtx = useContext(MeContext);
  const { fetchData } = meCtx;
  const { disableMfa, reEnableMfa } = useMfaApi();
  const { showSuccessToast, showErrorToast } = useMfaToast();
  const otpRequired = meCtx.state.account.otp_required;
  const otpMethod = meCtx.state.account.otp_method;
  const otpLastAuthenticated = new Date(
    meCtx.state.account.last_authenticated_at
  )
    .toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
    .replace(',', ' at');
  const [isDisableMfaOpen, setIsDisableMfaOpen] = useState(false);
  const [isMfaOperationOpen, setIsMfaOperationOpen] = useState(false);
  const [mfaOperation, setMfaOperation] = useState(null);

  const onDisableMfaOpen = () => {
    setIsDisableMfaOpen(true);
  };

  const onDisableMfaConfirm = async () => {
    try {
      const response = await disableMfa();
      if (response.status === 200) {
        showSuccessToast('MFA disabled');
        setIsDisableMfaOpen(false);
        fetchData();
      }
    } catch (error) {
      showErrorToast(
        'Error disabling MFA',
        'Failed to disable MFA. Please try again.'
      );
    }
  };

  const onReEnableMfa = async () => {
    try {
      const response = await reEnableMfa();
      if (response.status === 200) {
        showSuccessToast('MFA re-enabled');
        fetchData();
      }
    } catch (error) {
      showErrorToast(
        'Error re-enabling MFA',
        'Failed to re-enable MFA. Please try again.'
      );
    }
  };

  const onEnableMfaOpen = async () => {
    setMfaOperation('enable');
    setIsMfaOperationOpen(true);
  };

  const onChangeOtpMethodOpen = () => {
    setMfaOperation('change-method');
    setIsMfaOperationOpen(true);
  };

  const onResetAuthenticatorOpen = () => {
    setMfaOperation('reset');
    setIsMfaOperationOpen(true);
  };

  const onRegenerateRecoveryCodesOpen = () => {
    setMfaOperation('regenerate-codes');
    setIsMfaOperationOpen(true);
  };

  return (
    <Box
      className="autofillForDarkBg"
      w="full"
      borderRadius="15px"
      border="0.5px solid #D9D9D9"
      boxShadow="0px 5px 10px rgba(0, 0, 0, 0.05)"
      overflow="hidden"
    >
      <Box bg={modeBoxBg} borderRadius="15px">
        <Text
          fontSize="18px"
          textStyle="headingFamilyMedium"
          p="14px 14px 14px 24px"
          bg={modeTextBg}
          borderRadius="15px 15px 0 0"
        >
          Multi-factor authentication
        </Text>
        <Divider borderColor={modeBorderColor} />
        <VStack p={4} alignItems="flex-start" gap={4}>
          <HStack
            gap={4}
            justifyContent="flex-start"
            alignItems="center"
            bg="rgba(255, 71, 52, 0.08)"
            borderRadius="10px"
            p={4}
          >
            <GoShieldCheck fontSize="24px" color="rgba(255, 71, 52, 1)" />
            <VStack alignItems="flex-start">
              <Text textStyle="bodySmall" fontWeight="600">
                Secure your account
              </Text>
              <Text textStyle="bodySmall">
                Multi-factor authentication helps safeguard your account and
                customer data.
              </Text>
            </VStack>
          </HStack>
          <VStack alignItems="flex-start">
            <Text>MFA Status:</Text>
            <Text color={otpRequired ? 'green' : 'red'} textAlign="left">
              {otpRequired ? 'Enabled' : 'Disabled'}
            </Text>
            {otpRequired ? (
              <Text
                onClick={onDisableMfaOpen}
                cursor="pointer"
                color="brand"
                textStyle="bodySmall"
                textDecoration="underline"
              >
                Disable MFA
              </Text>
            ) : (
              <Text
                onClick={otpMethod ? onReEnableMfa : onEnableMfaOpen}
                cursor="pointer"
                color="brand"
                textStyle="bodySmall"
                textDecoration="underline"
              >
                Enable MFA
              </Text>
            )}
          </VStack>

          {otpRequired && (
            <>
              <Divider borderColor={modeBorderColor} />
              <VStack alignItems="flex-start">
                <Text>Method:</Text>
                <Text>
                  {otpMethod === 'totp' ? 'Authenticator app' : 'E-mail'}
                </Text>
                <HStack gap={8}>
                  <Text
                    onClick={onChangeOtpMethodOpen}
                    cursor="pointer"
                    color="brand"
                    textStyle="bodySmall"
                    textDecoration="underline"
                  >
                    Change method
                  </Text>
                  {otpMethod === 'totp' && (
                    <Text
                      onClick={onResetAuthenticatorOpen}
                      cursor="pointer"
                      color="brand"
                      textStyle="bodySmall"
                      textDecoration="underline"
                    >
                      Reset authenticator
                    </Text>
                  )}
                </HStack>
              </VStack>
              <Divider borderColor={modeBorderColor} />
              <VStack alignItems="flex-start">
                <Text>Last authenticated:</Text>
                <Text>{otpLastAuthenticated}</Text>
              </VStack>
              {otpMethod === 'totp' && (
                <>
                  <Divider borderColor={modeBorderColor} />
                  <VStack alignItems="flex-start">
                    <Text>Recovery codes:</Text>
                    <HStack>
                      <WarningIcon fontSize="12px" color="#666666" />
                      <Text textStyle="bodySmall" color="#666666">
                        You’ll need these if you ever lose access to your MFA
                      </Text>
                    </HStack>
                    <Text
                      onClick={onRegenerateRecoveryCodesOpen}
                      color="brand"
                      cursor="pointer"
                      textStyle="bodySmall"
                      textDecoration="underline"
                    >
                      Re-generate recovery codes
                    </Text>
                  </VStack>{' '}
                </>
              )}
            </>
          )}
        </VStack>
      </Box>
      <DisableMfaModal
        isOpen={isDisableMfaOpen}
        onClose={() => setIsDisableMfaOpen(false)}
        onConfirm={onDisableMfaConfirm}
      />
      <MfaOperationModal
        isOpen={isMfaOperationOpen}
        onClose={() => setIsMfaOperationOpen(false)}
        operation={mfaOperation}
        currentMethod={otpMethod}
      />
    </Box>
  );
}

export default MfaSettings;
